import axios from '@/axios.js'

/**
 * Source unique de vérité des montants d'un document client (devis, facture,
 * majoration).
 *
 * ---------------------------------------------------------------------------
 * POURQUOI CE MIXIN EXISTE
 * ---------------------------------------------------------------------------
 * La facture EU3026_00402 est partie chez le client avec un total de 12 910 €
 * alors que ses lignes affichaient 7 990 + 6 110 = 14 100 €. Cause : les lignes
 * imprimées et le total imprimé venaient de DEUX requêtes différentes —
 * `/getproductlistpdf` pour les lignes, `getinoutdifferenceeur` (via le store
 * Vuex `operationList.eurdifference`) pour le total. Quatre mécanismes les
 * faisaient diverger :
 *
 *   1. des conventions de signe incompatibles (une remise imprimée en positif
 *      mais soustraite du total) ;
 *   2. des lignes masquées par un INNER JOIN mais comptées dans le total ;
 *   3. un store Vuex global jamais réinitialisé : après modification d'une
 *      ligne, seul le volume était rafraîchi, jamais le montant en euros — le
 *      total imprimé restait celui d'AVANT la modification, voire celui de la
 *      facture consultée juste avant ;
 *   4. une course : le PDF était généré sans attendre la réponse du total.
 *
 * ---------------------------------------------------------------------------
 * RÈGLE
 * ---------------------------------------------------------------------------
 * Le total imprimé sur un document est TOUJOURS la somme des lignes imprimées
 * de ce document, renvoyée par l'API dans le même aller-retour qu'elles
 * (`/getdocumentlines`). Ne jamais lire `operationList.eurdifference` /
 * `rondifference` / `m3difference` pour un montant destiné à un PDF ou à un
 * écran de document : ces valeurs sont globales et peuvent appartenir à un
 * autre document.
 */

/** Tolérance d'écart admise entre la somme des lignes et le total (1 centime). */
const TOLERANCE = 0.01

function toNumber (value) {
  const n = parseFloat(value)
  return Number.isFinite(n) ? n : 0
}

function sumCents (lines, field) {
  let cents = 0
  for (const line of lines || []) {
    const n = parseFloat(line[field])
    if (Number.isFinite(n)) cents += Math.round(n * 100)
  }
  return cents / 100
}

/**
 * Montants du document, en fonctions PURES.
 *
 * Elles portent le corps EXACT des computed ci-dessous : le générateur e-factura
 * (helpers/efacturaUbl.js) travaille hors composant Vue et doit lire les mêmes
 * montants que l'écran. Deux implémentations du taux de TVA finiraient par
 * diverger — et le taux part dans un document légal.
 */
export function docHtRon (totals) {
  return totals ? toNumber(totals.ht_ron) : 0
}
export function docTtcRon (totals) {
  return totals ? toNumber(totals.ttc_ron) : 0
}
/**
 * Taux de TVA du document, lu sur la ventilation réelle des lignes.
 * Ne PAS revenir à `user_data.products[0].operations_products.tva_pct` :
 * `findOne` trie les produits par id, donc une ligne de remise (id négatif)
 * devenait products[0] et imposait son taux à toute la facture.
 */
export function docVatRateOf (totals) {
  const rates = (totals && totals.by_vat) || []
  const meaningful = rates.filter(
    (b) => toNumber(b.base_eur) !== 0 || toNumber(b.base_ron) !== 0
  )
  if (!meaningful.length) return 0
  return Math.max(...meaningful.map((b) => toNumber(b.tva_pct)))
}

/** Langues (people.idlanguage) : 1 EN, 2 FR, 3 RO, 4 DE, 5 ES, 6 NL. */
const LANG_EN = 1
const LANG_RO = 3

/**
 * Langue dans laquelle charger les lignes d'un document (`/getdocumentlines`).
 *
 * Une fiche client SANS langue (créée par un import, un agent ou une route
 * externe) faisait lever « Cannot read properties of null (reading 'id') »
 * dans `getProductListPdf` : les lignes et les totaux ne se chargeaient plus,
 * l'écran affichait 0,00 partout, et l'erreur sortait sous le titre
 * « les montants ne s'additionnent pas », qui n'avait rien à voir.
 *
 * Le repli ne sert qu'à l'AFFICHAGE : roumain si la fiche est roumaine
 * (TVA/CUI « RO… » ou pays RO), anglais sinon. La génération d'un PDF exige
 * une vraie langue, voir `documentLanguageMissing`.
 */
export function documentLanguageId (operation) {
  const person = operation && operation.people && operation.people[0]
  if (!person) return LANG_EN
  if (person.language && person.language.id) return Number(person.language.id)
  if (person.idlanguage) return Number(person.idlanguage)
  const isRo = (v) => typeof v === 'string' && /^\s*RO/i.test(v)
  if (isRo(person.vat_number) || isRo(person.cui) ||
      String(person.country_code_str || '').trim().toUpperCase() === 'RO') {
    return LANG_RO
  }
  return LANG_EN
}

/** true quand la fiche client du document n'a pas de langue. */
export function documentLanguageMissing (operation) {
  const person = operation && operation.people && operation.people[0]
  if (!person) return false
  return !(person.language && person.language.id) && !person.idlanguage
}

/** Erreur levée quand un document ne s'additionne pas : elle bloque le PDF. */
export class DocumentTotalsError extends Error {
  constructor (message, details) {
    super(message)
    this.name = 'DocumentTotalsError'
    this.details = details || {}
  }
}

export default {
  data () {
    return {
      /** Totaux du document courant, tels que renvoyés par l'API. */
      documentTotals: null
    }
  },
  computed: {
    /**
     * Montants du document courant. Ils remplacent partout
     * `$store.state.operationList.eurdifference / rondifference / m3difference`,
     * qui sont globaux, jamais réinitialisés entre deux documents et non
     * rafraîchis après modification d'une ligne.
     */
    docVolume () {
      return this.documentTotals ? Math.abs(toNumber(this.documentTotals.volume)) : 0
    },
    docTotalHtEur () {
      return this.documentTotals ? toNumber(this.documentTotals.ht_eur) : 0
    },
    docTotalHtRon () {
      return docHtRon(this.documentTotals)
    },
    docTotalTtcEur () {
      return this.documentTotals ? toNumber(this.documentTotals.ttc_eur) : 0
    },
    docTotalTtcRon () {
      return docTtcRon(this.documentTotals)
    },
    docPricePerM3 () {
      if (!this.docVolume) return 0
      return Math.abs(this.docTotalHtEur / this.docVolume)
    },
    /** Voir `docVatRateOf` ci-dessus. */
    docVatRate () {
      return docVatRateOf(this.documentTotals)
    },
    docVatLabel () {
      const rates = (this.documentTotals && this.documentTotals.by_vat) || []
      return rates.map((b) => b.tva_pct).join(' / ')
    },
    docHasVat () {
      const rates = (this.documentTotals && this.documentTotals.by_vat) || []
      return rates.some((b) => toNumber(b.tva_pct) > 0)
    }
  },
  methods: {
    /** Langue de chargement des lignes du document courant (voir documentLanguageId). */
    docLanguageId () {
      return documentLanguageId(this.user_data)
    },
    /** true si la fiche client du document courant n'a pas de langue. */
    docLanguageMissing () {
      return documentLanguageMissing(this.user_data)
    },
    /**
     * Un PDF client se rédige dans la langue de la fiche : sans elle, il n'y a
     * rien à générer. On le dit tel quel, au lieu de laisser une TypeError
     * sortir sous un titre qui parle de montants.
     */
    notifyMissingLanguage () {
      const person = this.user_data && this.user_data.people && this.user_data.people[0]
      const who = person
        ? (person.company_name || [person.firstname, person.lastname].filter(Boolean).join(' '))
        : ''
      this.$vs.notify({
        title: 'Customer language is not set',
        text: 'The document is written in the customer\'s language. Open the customer record' +
          (who ? ' (' + who + ')' : '') +
          ', set Language, then generate the document again.',
        color: 'danger',
        iconPack: 'feather',
        icon: 'icon-alert-triangle',
        position: 'top-center',
        time: 14000
      })
    },

    /**
     * Charge lignes ET totaux du document en un seul appel.
     * Retourne toujours une promesse : les appelants DOIVENT l'attendre avant
     * de générer un PDF (sinon on retombe sur la course d'origine).
     */
    fetchDocumentLines (idoperation, idlanguage, options) {
      // `endpoint` permet aux documents à tri particulier (bon de réception,
      // trié par catégorie/longueur/diamètre) d'utiliser leur propre requête
      // tout en obtenant le MÊME contrat { lines, totals } et la même règle de
      // calcul des montants.
      const endpoint = (options && options.endpoint) || '/getdocumentlines'
      const url = endpoint === '/getdocumentlines'
        ? `${endpoint}/${idoperation}/${idlanguage}`
        : `${endpoint}/${idoperation}/${idlanguage}?totals=1`
      return axios
        .get(url)
        .then((response) => {
          const payload = response.data || {}
          this.productlist = Array.isArray(payload.lines) ? [...payload.lines] : []
          this.documentTotals = payload.totals || null
          return payload
        })
    },

    /**
     * Vérifie que le document s'additionne, et REFUSE de le produire sinon.
     * À appeler juste avant toute génération de PDF ou tout envoi.
     * @throws {DocumentTotalsError}
     */
    assertDocumentConsistent (lines, totals, context) {
      const ctx = context || {}
      const label = ctx.documentLabel || ('#' + (ctx.idoperation || '?'))

      if (!totals) {
        throw new DocumentTotalsError(
          `Document ${label}: totals unavailable, generation cancelled.`,
          { idoperation: ctx.idoperation }
        )
      }

      if (Array.isArray(totals.warnings) && totals.warnings.length) {
        throw new DocumentTotalsError(
          `Document ${label}: ${totals.warnings[0]}`,
          { idoperation: ctx.idoperation, warnings: totals.warnings }
        )
      }

      const rows = lines || []
      const checks = [
        { field: 'amount_eur', total: totals.ht_eur, label: 'total excl. VAT (EUR)' },
        { field: 'amount_ron', total: totals.ht_ron, label: 'total excl. VAT (RON)' },
        { field: 'amount_ttc_eur', total: totals.ttc_eur, label: 'total incl. VAT (EUR)' },
        { field: 'amount_ttc_ron', total: totals.ttc_ron, label: 'total incl. VAT (RON)' }
      ]

      for (const check of checks) {
        const sum = sumCents(rows, check.field)
        const expected = toNumber(check.total)
        if (!Number.isFinite(expected) || Math.abs(sum - expected) > TOLERANCE) {
          throw new DocumentTotalsError(
            `Document ${label}: the printed ${check.label} (${expected.toFixed(2)}) ` +
            `does not match the sum of its ${rows.length} line(s) (${sum.toFixed(2)}), ` +
            `difference ${(expected - sum).toFixed(2)}. Generation cancelled.`,
            {
              idoperation: ctx.idoperation,
              field: check.field,
              sumOfLines: sum,
              printedTotal: expected
            }
          )
        }
      }
      return true
    },

    /**
     * Document de stock (réception…) dont des lignes n'ont pas encore de prix :
     * l'API les range dans `totals.missing_prices` au lieu de bloquer. Le
     * document se génère, ces lignes à 0.00 ; on le signale sans l'empêcher.
     */
    notifyMissingPrices (totals) {
      const missing = (totals && totals.missing_prices) || []
      if (!missing.length) return
      const names = missing.map((m) => m.name || ('product ' + m.idproduct))
      this.$vs.notify({
        title: missing.length === 1 ? '1 line has no price yet' : missing.length + ' lines have no price yet',
        text: 'Printed at 0.00: ' + names.join(', ') + '.',
        color: 'warning',
        iconPack: 'feather',
        icon: 'icon-info',
        position: 'top-center',
        time: 8000
      })
    },

    /**
     * Signale un document incohérent : notification bloquante + trace serveur
     * dans le journal d'audit (visible dans le FULL LOG admin).
     */
    reportDocumentTotalsError (error, context) {
      const ctx = context || {}
      // Le titre dit ce qui s'est VRAIMENT passé : un écart de montants, ou une
      // autre erreur attrapée par le même garde-fou (une TypeError sur une
      // fiche incomplète, par exemple). Afficher « les montants ne
      // s'additionnent pas » pour un `Cannot read properties of null` envoyait
      // chercher un problème de totaux qui n'existait pas.
      const isTotals = !!error && (error.name === 'DocumentTotalsError' || error.isEfacturaTotalsMismatch)
      // Une ligne incomplète (prix absent, produit supprimé) n'est pas un écart
      // de montants : le dire tel quel.
      const isIncomplete = isTotals && !!(error.details && error.details.warnings)
      this.$vs.notify({
        title: isIncomplete
          ? 'Document blocked: incomplete line'
          : isTotals
            ? 'Document blocked: amounts do not add up'
            : 'Document blocked: generation failed',
        text: error.message,
        color: 'danger',
        iconPack: 'feather',
        icon: 'icon-alert-triangle',
        position: 'top-center',
        time: 20000
      })
      // eslint-disable-next-line no-console
      console.error('[documentTotals]', error.message, error.details)

      if (ctx.idoperation) {
        axios
          .post('/auditlog', {
            entity: 'operation',
            entityId: ctx.idoperation,
            idoperation: ctx.idoperation,
            message: (isTotals ? 'Document generation blocked (amounts): ' : 'Document generation blocked: ') + error.message
          })
          .catch(() => { /* le blocage prime : ne jamais masquer l'erreur d'origine */ })
      }
    },

    /**
     * Construit la ligne « TOTAL » du tableau PDF à partir des totaux du
     * document — jamais depuis le store Vuex.
     *
     * @param {object} totals   `this.documentTotals`
     * @param {string} label    libellé traduit (« Total », « Totaal »…)
     * @param {string} currency 'eur' | 'ron'
     */
    buildTotalRow (totals, label, currency) {
      const isRon = String(currency).toLowerCase() === 'ron'
      const ht = toNumber(isRon ? totals.ht_ron : totals.ht_eur)
      const ttc = toNumber(isRon ? totals.ttc_ron : totals.ttc_eur)
      // Un seul taux sur le document -> on l'affiche ; plusieurs taux -> la
      // colonne reste vide et la ventilation est imprimée sous le tableau
      // (afficher le taux de la 1re ligne donnait un TTC faux).
      const rates = (totals.by_vat || []).filter((b) => toNumber(b.base_eur) !== 0 || toNumber(b.base_ron) !== 0)
      const singleRate = rates.length === 1 ? rates[0].tva_pct : null

      const row = {
        description: label,
        volumetotal: Math.abs(toNumber(totals.volume)).toFixed(4),
        tva_pct: singleRate === null ? '' : singleRate
      }
      if (isRon) {
        row.pricetotalron = this.formatMillier(ht.toFixed(2))
        row.total_tva_ron = this.formatMillier(ttc.toFixed(2))
      } else {
        row.pricetotal = this.formatMillier(ht.toFixed(2))
        row.total_tva = this.formatMillier(ttc.toFixed(2))
      }
      return row
    },

    /** true si le document porte plusieurs taux de TVA (ventilation à imprimer). */
    hasMultipleVatRates (totals) {
      if (!totals || !Array.isArray(totals.by_vat)) return false
      return totals.by_vat.length > 1
    }
  }
}
